import type {
  FeatureAnnouncementPageDefinition,
  MonthGroup,
} from '@/cmsBackend/transformers/types/featureAnnouncement';
import { useTheme } from '@/common/contexts/ContextProvider';
import { Env, Theme } from '@/common/types';
import ctaButtonStyles from '@/components/CTA/CTA.module.scss';
import { FeatureAnnouncementHero } from '@/components/modules/FeatureAnnouncementHero';
import { MonthGroupSection } from '@/components/modules/MonthGroupSection';
import { ProductUpdatesLoadingSkeleton } from '@/components/modules/ProductUpdatesLoadingSkeleton';
import { ProductUpdatesMonthSelector } from '@/components/modules/ProductUpdatesMonthSelector';
import { OtherImprovementsSection } from '@/components/modules/OtherImprovementsSection';
import { SocialSection } from '@/components/modules/SocialSection';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { fetchProductUpdatesMonthGroup } from './fetchProductUpdatesMonthGroup';
import { mergeMonthGroupIntoList } from './mergeMonthGroupIntoList';
import styles from './FeatureAnnouncementContainer.module.scss';
import { messages } from './messages';
import { useProductUpdatesHashDeepLink } from './useProductUpdatesHashDeepLink';

const NavbarComponent = dynamic(
  () =>
    import(/* webpackChunkName: "navbar_component" */ '@/components/global').then(
      (mod) => mod.Navbar,
    ),
  { ssr: true, loading: () => null },
);

const FooterComponent = dynamic(
  () =>
    import(/* webpackChunkName: "footer_component" */ '@/components/global').then(
      (mod) => mod.Footer,
    ),
  { ssr: true },
);

export interface FeatureAnnouncementContainerProps {
  env: Env;
  pageData: FeatureAnnouncementPageDefinition;
}

export const FeatureAnnouncementContainer: React.FC<FeatureAnnouncementContainerProps> = ({
  pageData,
  env,
}) => {
  const { setPageTheme } = useTheme();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (pageData.theme && pageData.theme !== '') {
      setPageTheme(pageData.theme as Theme);
    }
  }, [pageData.theme, setPageTheme]);

  const [monthGroups, setMonthGroups] = useState<MonthGroup[]>(pageData.monthGroups);
  const [hasMore, setHasMore] = useState(pageData.hasMore);
  // Set while organic scroll OR a chip click is waiting on more months to
  // load. There is only ever one "next month" being fetched at a time, so a
  // single flag drives the single trailing skeleton below.
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Set while a chip click is chasing an unloaded month, so that chip can
  // show a busy indicator. Also doubles as the concurrency guard: a second
  // chip click is ignored while this is set (see handleMonthSelect).
  const [pendingMonthKey, setPendingMonthKey] = useState<string>();
  const [activeMonthKey, setActiveMonthKey] = useState(pageData.monthGroups[0]?.key);
  const cursorRef = useRef<string | undefined>(pageData.nextCursor);
  const hasMoreRef = useRef(pageData.hasMore);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const monthSelectorRef = useRef<HTMLDivElement>(null);
  // In-flight loadMoreMonth call, if any. A second caller (e.g. a chip click
  // arriving while the sentinel's organic scroll is already fetching) joins
  // this promise instead of getting an immediate `false`, since that would
  // otherwise be indistinguishable from "no more months" and abort its loop.
  const loadMorePromiseRef = useRef<Promise<boolean> | undefined>(undefined);

  // The only place that fetches more months — used both by the sentinel's
  // IntersectionObserver (organic scroll, below) and by chip clicks that
  // target an unloaded month (handleMonthSelect, below).
  const loadMoreMonth = useCallback((): Promise<boolean> => {
    if (loadMorePromiseRef.current) {
      return loadMorePromiseRef.current;
    }

    const currentCursor = cursorRef.current;
    if (!currentCursor) {
      return Promise.resolve(false);
    }

    const promise = (async () => {
      setIsLoadingMore(true);
      try {
        const data = await fetchProductUpdatesMonthGroup(currentCursor, pageData.locale);
        if (data.monthGroup) {
          cursorRef.current = data.nextCursor;
          hasMoreRef.current = data.hasMore;
          setHasMore(data.hasMore);
          setMonthGroups((prev) => mergeMonthGroupIntoList(prev, data.monthGroup!));
          return true;
        }
        if (!data.hasMore && !data.nextCursor) {
          hasMoreRef.current = false;
          setHasMore(false);
        }
        return false;
      } catch (error) {
        console.error('[FeatureAnnouncementContainer] Load more error:', error);
        return false;
      } finally {
        loadMorePromiseRef.current = undefined;
        setIsLoadingMore(false);
      }
    })();

    loadMorePromiseRef.current = promise;
    return promise;
  }, [pageData.locale]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === 'undefined') {
      return;
    }

    // IntersectionObserver only fires on a threshold crossing, not on every
    // frame the sentinel remains intersecting — if a loaded month is short
    // enough that the sentinel never leaves the rootMargin, the callback
    // never fires again and loading silently stalls. Re-observing after
    // each load forces a fresh notification of the sentinel's current
    // state, so loading continues for as long as it's still in range.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }
        void loadMoreMonth().then((appended) => {
          if (appended && hasMoreRef.current) {
            observer.unobserve(sentinel);
            observer.observe(sentinel);
          }
        });
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMoreMonth]);

  // Derived from the selector's own sticky offset + height rather than its
  // live getBoundingClientRect().bottom, because that rect only reflects the
  // selector's *stuck* position once the page has actually scrolled past its
  // sticky threshold. Near the top of the page (before it sticks) the rect
  // still reports the selector's natural, much lower position, which would
  // undershoot the scroll target on the very first click.
  const getMonthActivationLine = () => {
    const selector = monthSelectorRef.current;
    if (!selector) {
      return 0;
    }
    const stickyTop = parseFloat(getComputedStyle(selector).top) || 0;
    return stickyTop + selector.offsetHeight;
  };

  useEffect(() => {
    const sections = monthGroups
      .map((group) => document.getElementById(`month-${group.key}`))
      .filter((section): section is HTMLElement => Boolean(section));
    if (sections.length === 0) {
      return;
    }

    let animationFrame: number | undefined;
    const updateActiveMonth = () => {
      animationFrame = undefined;
      const activationLine = getMonthActivationLine();
      let currentSection = sections[0];

      for (const section of sections) {
        if (section.getBoundingClientRect().top > activationLine + 1) {
          break;
        }
        currentSection = section;
      }

      const monthKey = currentSection.getAttribute('data-month-key');
      if (monthKey) {
        setActiveMonthKey((currentKey) => (currentKey === monthKey ? currentKey : monthKey));
      }
    };
    const requestUpdate = () => {
      if (animationFrame === undefined) {
        animationFrame = requestAnimationFrame(updateActiveMonth);
      }
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    requestUpdate();

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [monthGroups]);

  const scrollMonthToActivationLine = useCallback((target: HTMLElement) => {
    window.scrollTo({
      top: window.scrollY + target.getBoundingClientRect().top - getMonthActivationLine(),
      behavior: 'smooth',
    });
  }, []);

  // Chip clicks reuse the same loadMoreMonth used for organic infinite
  // scroll (there is only one place that fetches). If the target isn't
  // loaded yet: mark the chip pending (busy spinner + ignore other chip
  // clicks until this resolves, since a second concurrent loop against the
  // shared loadMoreMonth would starve the first one) and keep fetching
  // further months until it appears, then scroll to it. No scrolling
  // happens until the target is actually loaded.
  const handleMonthSelect = useCallback(
    async (monthKey: string) => {
      if (pendingMonthKey) {
        return;
      }

      let target = document.getElementById(`month-${monthKey}`);
      if (target) {
        scrollMonthToActivationLine(target);
        return;
      }

      setPendingMonthKey(monthKey);

      try {
        let lastLoadAppended = false;
        while (!target && hasMoreRef.current && cursorRef.current) {
          lastLoadAppended = await loadMoreMonth();
          if (!lastLoadAppended) {
            break;
          }
          await new Promise(requestAnimationFrame);
          target = document.getElementById(`month-${monthKey}`);
        }

        // If the append that completed the archive (hasMore just became
        // false) was also the one containing the target, the loop above
        // exits right after it without a further iteration to allow React
        // more time to commit — give it one more frame before giving up.
        if (!target && lastLoadAppended) {
          await new Promise(requestAnimationFrame);
          target = document.getElementById(`month-${monthKey}`);
        }

        if (target) {
          scrollMonthToActivationLine(target);
        }
      } finally {
        setPendingMonthKey(undefined);
      }
    },
    [pendingMonthKey, loadMoreMonth, scrollMonthToActivationLine],
  );

  useProductUpdatesHashDeepLink({
    monthGroups,
    hasMore,
    locale: pageData.locale,
    loadMoreMonth,
  });

  // availableMonthKeys can be an empty array (e.g. the month index query
  // failed) even though monthGroups already has content, so fall back on
  // emptiness, not just null/undefined. Deduplicated defensively: the
  // selector must never render two chips for the same month regardless of
  // what the upstream data contains.
  const monthKeysForSelector = Array.from(
    new Set(
      pageData.availableMonthKeys?.length
        ? pageData.availableMonthKeys
        : monthGroups.map((group) => group.key),
    ),
  );

  return (
    <>
      {pageData.includeNavbar && pageData.navigation && (
        <NavbarComponent {...pageData.navigation} />
      )}

      <main id="main-content" className={styles.main}>
        <FeatureAnnouncementHero
          title={pageData.hero.title ?? ''}
          subtitle={pageData.hero.subtitle}
          counterText={pageData.hero.counterText}
          image={pageData.hero.image}
          fallbackMedia={
            pageData.monthGroups[0]?.majorAnnouncements
              .filter((card) => card.media)
              .map((card) => ({
                type: card.media!.type,
                src: card.media!.src,
                alt: card.media!.alt,
              })) ?? []
          }
          cta={pageData.hero.cta}
          breadcrumbs={{
            items: [
              { label: 'Home', link: '/' },
              { label: formatMessage(messages.breadcrumbProductUpdates), isCurrentPage: true },
            ],
            position: 'page-top',
            density: 'default',
            alignment: 'left',
          }}
          isFirstModule={true}
        />

        <ProductUpdatesMonthSelector
          months={monthKeysForSelector.map((key) => {
            const loadedMonth = monthGroups.find((group) => group.key === key);
            if (loadedMonth) {
              return loadedMonth;
            }
            const [year, month] = key.split('-').map(Number);
            const label = new Intl.DateTimeFormat(pageData.locale, {
              month: 'long',
              year: 'numeric',
              timeZone: 'UTC',
            }).format(new Date(Date.UTC(year, month - 1)));
            return {
              key,
              label,
              monthName: label.split(' ')[0],
              year: String(year),
              count: 0,
              isCurrentMonth: false,
              majorAnnouncements: [],
              minorAnnouncements: [],
            };
          })}
          activeMonthKey={activeMonthKey}
          pendingMonthKey={pendingMonthKey}
          onMonthSelect={handleMonthSelect}
          selectorRef={monthSelectorRef}
          className={styles.monthSelector}
        />

        {monthGroups.map((group) => (
          <React.Fragment key={group.key}>
            <MonthGroupSection
              monthGroup={group}
              heading={
                group.isCurrentMonth
                  ? (pageData.currentMajorAnnouncementsHeading ?? '')
                  : (pageData.pastMajorAnnouncementsHeading ?? '')
              }
              // This month is the target of a chip-triggered scroll about to
              // land on it — skip the reveal animation so it doesn't render
              // invisible for a moment while the jump lands.
              skipRevealAnimation={group.key === pendingMonthKey}
            />
            <OtherImprovementsSection
              heading={pageData.minorAnnouncementsHeading ?? ''}
              items={group.minorAnnouncements}
            />
          </React.Fragment>
        ))}

        {/* One trailing placeholder covers both organic scroll-to-bottom
            loading and chip clicks chasing an unloaded month — there is
            only ever one "next month being loaded" at a time. */}
        {isLoadingMore && hasMore && <ProductUpdatesLoadingSkeleton />}
        <div ref={loadMoreSentinelRef} className={styles.loadMoreSentinel} />

        {pageData.secondaryCta && (
          <div className={styles.secondaryCtaWrapper}>
            <a
              href={pageData.secondaryCta.link}
              target="_blank"
              rel="noopener noreferrer"
              className={classNames(
                ctaButtonStyles['cta-btn'],
                ctaButtonStyles[`cta-btn_type__${pageData.secondaryCta.type.toLowerCase()}`],
              )}
            >
              {pageData.secondaryCta.label}
            </a>
          </div>
        )}

        {pageData.showShareSection && pageData.shareSectionLink && (
          <SocialSection buttonLink={pageData.shareSectionLink} />
        )}
      </main>

      {pageData.includeFooter && (
        <FooterComponent env={env} marketingSectionData={pageData.footer} />
      )}
    </>
  );
};
